// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Server-side re-validation (M9-26, 11 §5, 11 §8).
//
// *"POST /machines/{id}/versions/{semver}/publish → Server-side validation — the same 16 classes,
// re-run — **never trusts the client**."*
//
// **"Never trusts the client" is not a slogan here, it is the threat model.** The Builder is a
// JavaScript bundle on somebody else's machine. An author who wants to publish a machine the
// validator refuses does not need to find a bug in it — they open the console and `POST` the
// document. So every check the Builder runs is run again here, from the bytes as received, with
// nothing taken from the request but the bytes.
//
// **The same validator, not a second one.** `_shared/core/machine/validate.ts` is generated from
// `core/ts/machine/validate.ts` by `scripts/generate-contracts.mjs` and a test compares the two
// line for line. A hand-written server check would be a second opinion, and the first time the two
// disagreed the author would be told their machine is fine and then refused, or worse, told it is
// broken when it is not.
//
// **And three things the client cannot be trusted on at all**, which is why this is more than a
// call to `validateMachine`:
//
//  1. **The budgets of 11 §8.** They are enforced "at save, again at publish, and again at load",
//     and most of them are not in the validator at all — it bounds an art surface and a display
//     scene, and says nothing about 120 devices or a 512 KB document. A client that skipped them
//     ships a machine that costs everyone else's device.
//  2. **`meta.author.kind`.** `first-party` is a claim about who wrote a machine, and it is the one
//     field in the document whose truth lives on the server. A player-published machine claiming it
//     would sit in the arcade wearing the house's name.
//  3. **The size of the thing itself**, measured before it is parsed. A 40 MB document that fails
//     validation still cost 40 MB of memory to find that out.
//
// Pure: no database, no network. It takes bytes and answers.

import {
  BUDGET_CODE,
  BUDGETS,
  overBudget as overBudgetTable,
  validateMachine,
  type MachineDefinition,
  type ValidationIssue,
} from '@core/machine'

/**
 * 11 §8's table, re-exported from the core.
 *
 * **One table, three call sites** (M9-30). The milestone's own wording is "enforced at save, again
 * at publish, and again at load", and three enforcements of one rule is three chances to disagree
 * about it — so the numbers live in `core/ts/machine/budgets.ts` and this asks that table rather
 * than carrying a second copy of the number 120.
 */
export { BUDGETS }

/**
 * 11 §8's document limit, in bytes.
 *
 * **The one budget the core does not carry**, and deliberately: measuring it needs `TextEncoder`,
 * which the core's purity rule refuses along with the very word `document`. It belongs here anyway
 * — the limit is on a *request body*, and this is the only one of the three call sites that holds
 * bytes rather than an object.
 */
export const DOCUMENT_BUDGET = 512 * 1024

/** Whether a body is small enough to be worth parsing. Measured in UTF-8 bytes, not characters. */
export function overDocumentBudget(raw: string): { path: string; message: string } | undefined {
  const bytes = new TextEncoder().encode(raw).length
  if (bytes <= DOCUMENT_BUDGET) return undefined
  return {
    path: 'document',
    message: `${bytes} bytes exceeds the ${DOCUMENT_BUDGET} a definition may be`,
  }
}

export interface PublishRefusal {
  readonly code: string
  readonly path: string
  readonly message: string
}

export interface PublishVerdict {
  readonly ok: boolean
  /** Everything that refuses this machine, worst first. Empty when it may publish. */
  readonly refusals: readonly PublishRefusal[]
  /** Legal but worth recording against the version. Never blocks. */
  readonly warnings: readonly ValidationIssue[]
}

export interface PublishContext {
  /**
   * Whether the caller is allowed to claim `meta.author.kind: "first-party"`.
   *
   * **Decided by the server from the session, never read from the document.** The document is the
   * thing being checked; a field in it cannot be its own authority.
   */
  readonly firstParty?: boolean
}

/**
 * Re-run everything, on the bytes as received.
 *
 * `raw` is the request body exactly as it arrived. It is measured, then parsed here rather than by
 * the caller, so a document that is not JSON is a refusal with a reason rather than an exception
 * three frames up.
 */
export function revalidateForPublish(raw: string, context: PublishContext = {}): PublishVerdict {
  const refusals: PublishRefusal[] = []

  // Measured before parsing. A 40 MB document that fails validation still cost 40 MB to find out.
  const oversize = overDocumentBudget(raw)
  if (oversize !== undefined) {
    return {
      ok: false,
      warnings: [],
      refusals: [{ code: BUDGET_CODE, path: oversize.path, message: oversize.message }],
    }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (error) {
    return {
      ok: false,
      warnings: [],
      refusals: [
        {
          code: 'not-json',
          path: 'document',
          message: error instanceof Error ? error.message : 'the document is not JSON',
        },
      ],
    }
  }

  // The same sixteen classes, with the flood passes on. The client runs these too; that is the
  // point — this is the copy that decides.
  const report = validateMachine(parsed as MachineDefinition, { flood: true })
  for (const issue of report.errors) {
    refusals.push({ code: issue.code, path: issue.path, message: issue.message })
  }

  refusals.push(...overBudget(parsed))
  refusals.push(...misclaimed(parsed, context))

  return { ok: refusals.length === 0, refusals, warnings: report.warnings }
}

/**
 * 11 §8's budgets, which the validator mostly does not know about.
 *
 * It bounds an art surface and a display scene because those are load-time costs the *renderer*
 * cares about. The rest — 120 devices, 400 colliders, 160 lamps — are budgets about somebody else's
 * phone, and nothing in the format refuses them.
 *
 * The counting itself is the core's (M9-30), so the number this refuses on is the number the
 * Builder warned about and the number the client will refuse to load.
 */
export function overBudget(definition: unknown): readonly PublishRefusal[] {
  return overBudgetTable(definition).map((overrun) => ({
    code: BUDGET_CODE,
    path: overrun.path,
    message: overrun.message,
  }))
}

/**
 * Claims about the machine that only the server can settle.
 *
 * `meta.author.kind: "first-party"` is the house's own name on a machine. It is a fact about the
 * *account* that published it, and a document cannot be its own authority for a fact about
 * something outside it.
 */
export function misclaimed(definition: unknown, context: PublishContext): readonly PublishRefusal[] {
  if (!isRecord(definition)) return []
  const meta = isRecord(definition['meta']) ? definition['meta'] : undefined
  const author = meta !== undefined && isRecord(meta['author']) ? meta['author'] : undefined
  if (author?.['kind'] !== 'first-party') return []
  if (context.firstParty === true) return []
  return [
    {
      code: 'not-first-party',
      path: 'meta.author.kind',
      message: 'only the house may publish a machine as first-party',
    },
  ]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
